import { type ErrorComponentProps, SearchParamError } from '@tanstack/react-router'
import { TRPCClientError } from '@trpc/client'
import { memo } from 'react'

import { ensureError } from '@orus.eu/error'
import { FunctionnalProblemError, InvalidLinkError, NotFoundError, TemporaryProblemError } from '@orus.eu/pharaoh'

import { TrpcErrorHandler } from '../../lib/trpc-errorhandler.tsx'
import { HelpContact } from '../../modules/support/shared/help-contact-dialog/help-contact.tsx'
import { CrashPage } from '../organisms/crash-page.tsx'
import { GenericProblemMessage } from '../organisms/generic-problem-message.tsx'
import { InvalidLinkMessage } from '../organisms/invalid-link-message.tsx'
import { TemporaryFailureScreen } from '../organisms/temporary-failure-screen.tsx'

import { NotFoundPage } from '#/shared/components/not-found-page/not-found-page.tsx'

export const RootErrorBoundary = memo(function RootErrorBoundary(props: ErrorComponentProps) {
  return (
    <HelpContact.Root>
      <ErrorBoundary {...props} />
    </HelpContact.Root>
  )
})

const ErrorBoundary = memo(function ErrorBoundary(props: ErrorComponentProps) {
  // We saw bots replace the global `Error`. Their network errors then failed `instanceof Error` and were no longer
  // recognized as network errors.
  const isNativeError = Object.prototype.toString.call(props.error) === '[object Error]'
  const error = isNativeError ? props.error : ensureError(props.error)

  if (error instanceof TRPCClientError) {
    return <TrpcErrorHandler err={error} />
  }

  if (error instanceof TemporaryProblemError) {
    return <TemporaryFailureScreen />
  }

  if (error instanceof FunctionnalProblemError) {
    return <GenericProblemMessage {...error.params} />
  }

  if (error instanceof NotFoundError || error instanceof SearchParamError) {
    return <NotFoundPage />
  }

  if (error instanceof InvalidLinkError) {
    return <InvalidLinkMessage />
  }

  return <CrashPage error={error} />
})
